import { WindowTitle } from "@saleor/components/WindowTitle";
import { Task } from "@saleor/containers/BackgroundTasks/types";
import useBackgroundTask from "@saleor/hooks/useBackgroundTask";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import useShop from "@saleor/hooks/useShop";
import { sectionNames } from "@saleor/intl";
import { changeTimeZone } from "@saleor/utils/smop/changeTimeZoneDateTime";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import { decimal, maybe } from "../../misc";
import {
  DiscountValueTypeEnum,
  VoucherDiscountType,
  VoucherTypeEnum
} from "../../types/globalTypes";
import VoucherCreatePage from "../components/VoucherCreatePage";
import useVoucherLimitsData from "../hooks/useVoucherLimitsData";
import { TypedVoucherCreate, useVoucherBulkCreate } from "../mutations";
import { RequirementsPicker } from "../types";
import { VoucherBulkCreate } from "../types/VoucherBulkCreate";
import { VoucherCreate } from "../types/VoucherCreate";
import { bulkVoucherListUrl, voucherListUrl, voucherUrl } from "../urls";

export const VoucherDetails: React.FC = () => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const shop = useShop();
  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const { queue } = useBackgroundTask();
  const {
    voucherLimitDispatcher,
    voucherLimitState,
    voucherConditionCreateApi
  } = useVoucherLimitsData();

  const handleVoucherCreate = (data: VoucherCreate) => {
    if (data.voucherCreate.errors.length === 0) {
      // queue()
      if (voucherLimitState.isLimitToCustomerActivity) {
        voucherConditionCreateApi(data.voucherCreate.voucher.id)
          .then(() => {
            notify({
              status: "success",
              text: intl.formatMessage({
                defaultMessage: "Voucher Created"
              })
            });
          })
          .catch(() => {
            notify({
              status: "error",
              text: intl.formatMessage({
                defaultMessage: "Failed to create voucher condition(s)."
              })
            });
          });
      } else {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Voucher Created"
          })
        });
      }

      navigate(voucherUrl(data.voucherCreate.voucher.id), true);
    } else if (
      data.voucherCreate.errors.some(e => e.field === "discountShare")
    ) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "The discount distribution is invalid."
        })
      });
    }
  };
  const handleVoucherBulkCreate = (data: VoucherBulkCreate) => {
    if (data.voucherBulkCreate.discountErrors.length === 0) {
      queue(Task.BULK_VOUCHER, {
        id: data.voucherBulkCreate.voucherJob.id,
        onResponseData: jobData => {
          if (jobData && jobData.data) {
            if (jobData.data.voucherJob.status === "PENDING") {
              setLoading(true);
            } else if (jobData.data.voucherJob.status === "SUCCESS") {
              setTimeout(() => {
                navigate(
                  bulkVoucherListUrl(
                    data.voucherBulkCreate.voucherJob.voucherGroup.id
                  )
                );
              }, 2000);
            }
          }
        }
      });
    } else if (
      data.voucherBulkCreate.discountErrors[0].field === "multiplier" &&
      data.voucherBulkCreate.discountErrors[0].code === "INVALID"
    ) {
      notify({
        status: "error",
        text: data.voucherBulkCreate.discountErrors[0].message
      });
    }
  };

  const [voucherBulkCreate] = useVoucherBulkCreate({
    onCompleted: handleVoucherBulkCreate
  });

  return (
    <TypedVoucherCreate onCompleted={handleVoucherCreate}>
      {(voucherCreate, voucherCreateOpts) => (
        <>
          <WindowTitle title={intl.formatMessage(sectionNames.vouchers)} />
          <VoucherCreatePage
            defaultCurrency={maybe(() => shop.defaultCurrency)}
            disabled={voucherCreateOpts.loading || loading}
            errors={voucherCreateOpts.data?.voucherCreate.errors || []}
            onBack={() => navigate(voucherListUrl())}
            onSubmit={formData => {
              const voucherDetail = {
                applyOncePerCustomer: formData.applyOncePerCustomer,
                applyOncePerOrder: formData.applyOncePerOrder,

                deliveryTypes: formData.deliveryType,
                description: formData.description,
                discountValue:
                  formData.discountType.toString() === "SHIPPING"
                    ? 100
                    : decimal(formData.value),

                discountValueType:
                  formData.discountType.toString() === "SHIPPING"
                    ? DiscountValueTypeEnum.PERCENTAGE
                    : formData.discountType.toString() ===
                      VoucherDiscountType.PERCENTAGE_AND_CAPPED
                    ? DiscountValueTypeEnum.PERCENTAGE
                    : formData.discountType.toString() ===
                      VoucherDiscountType.E_VOUCHER
                    ? DiscountValueTypeEnum.FIXED
                    : formData.discountType,
                endDate: changeTimeZone(formData.endDate, formData.endTime),
                isAppliedOnEligibleItems: formData.isAppliedOnEligibleItems,
                isAppliedToAllMalls: true,
                isHidden: formData.isHidden,
                isMinEligibleOnly: formData.isMinEligibleOnly,
                isSpecificCustomer: false,
                maxDiscountValue:
                  formData.discountType.toString() ===
                  VoucherDiscountType.PERCENTAGE_AND_CAPPED
                    ? decimal(formData.maxDiscountValue)
                    : null,
                minAmountSpent:
                  formData.requirementsPicker !== RequirementsPicker.ORDER
                    ? 0
                    : parseFloat(formData.minSpent),
                minCheckoutItemsQuantity:
                  formData.requirementsPicker !== RequirementsPicker.ITEM
                    ? 0
                    : parseFloat(formData.minCheckoutItemsQuantity),
                minTenants: formData.minTenants,
                // paymentMethod: formData.paymentType,
                smopDiscountShare:
                  formData.discountType.toString() === "SHIPPING"
                    ? 0
                    : formData?.smopDiscountShare,
                startDate: changeTimeZone(
                  formData.startDate,
                  formData.startTime
                ),
                tenantDiscountShare:
                  formData.discountType.toString() === "SHIPPING"
                    ? 0
                    : formData.tenantDiscountShare,
                type:
                  formData.discountType.toString() === "SHIPPING"
                    ? VoucherTypeEnum.SHIPPING
                    : formData.discountType.toString() === "E_VOUCHER"
                    ? VoucherTypeEnum.E_VOUCHER
                    : formData.type,
                usageLimit: formData.hasUsageLimit
                  ? parseInt(formData.usageLimit, 10)
                  : null,
                verticalType: formData.verticalType
              };
              if (formData.bulkCreate) {
                voucherBulkCreate({
                  variables: {
                    multiplier: formData.multiplier,
                    voucherDetails: {
                      ...voucherDetail,
                      groupCode: formData.code
                    }
                  }
                });
              } else {
                voucherCreate({
                  variables: {
                    input: { ...voucherDetail, code: formData.code }
                  }
                });
              }
            }}
            saveButtonBarState={voucherCreateOpts.status}
            voucherLimitState={voucherLimitState}
            voucherLimitDispatcher={voucherLimitDispatcher}
          />
        </>
      )}
    </TypedVoucherCreate>
  );
};
export default VoucherDetails;
